/**
 * トースト (操作の結果を伝える知らせ)。2026-09-21「上に出る知らせ」G 案で作り直した。
 *
 * 考え方 (X と同じ):
 *   - 操作の結果は **画面の下** に出す (スマホはボトムナビの上、PC はメインの列の下端)。
 *     上は「引っ張って更新」と「新着ポスト」の場所 (topnotice.ts) で、ここと混ぜない。
 *   - **同時に出すのは 1 つだけ**。次が来たら前のものは下へ引っ込む (積まない)。
 *     同じ文がもう出ていれば、出し直さずに時間だけ延ばす。
 *   - 面は落ち着いた紺 (ライトは白)。**意味の色はアイコンだけ** — 成功 ✓ 緑 / 情報 i 青 /
 *     注意 ! 黄 / エラー ✗ ピンク。面を色で塗らない (エラーまでお祝いの色にしない)。
 *   - 右端に 1 つだけ行動を置ける (「表示」「元に戻す」「再試行」「詳細」…)。
 *     押せる高さは 44px。行動があるものは長めに残す。
 *   - 長い文は 3 行まで折り返し、それ以上は「…」。全文は title と読み上げに残す。
 *   - 出ている間は右下の＋ボタン (FAB) をその高さぶん持ち上げる。
 *   - 触っている間 (指・マウス・キーボードの焦点) は消えない。下へ払うと消える。
 *
 * ⚠ opacity プロパティで出し入れしない (ライトの `body.theme-light * { opacity:1 !important }`)。
 *   transform / visibility / filter: opacity() を使う。
 * ⚠ 文字色は !important (ライトの全称ルールが color を紺に戻す)。CSS は v2.css §256。
 * ⚠ DOM の形 (.mp-toast > .mp-toast-inner、行動は a.mp-toast-earn) は interactions.ts /
 *   limits.ts の後付けリンクと契約テストが見ているので変えない。
 *
 * 使い方:
 *   showToast("ピン留めしました", "success");
 *   showToast("ポストを削除しました", "success", { action: { label: "元に戻す", onClick: undo } });
 *   window.showToast("URLをコピーしました")   // 手書きの JS から
 *
 * URL クエリ ?flash=msg&flash_type=success が付いていれば自動で出してから history を置き換える。
 */

export type ToastType = "info" | "success" | "warn" | "error";

export interface ToastAction {
  label: string;
  href?: string;
  onClick?: (ev: MouseEvent) => void;
  /** 属性を足す (例: data-composer-trigger) */
  attrs?: Record<string, string>;
}

export interface ToastOpts {
  /** 0 で自動では消さない。省略時は文の長さと種類から決める */
  duration?: number;
  type?: ToastType;
  /** インライン SVG を明示したいとき (通常は種類のアイコン) */
  icon?: string;
  action?: ToastAction;
}

/* 線画のアイコン (他の画面と同じ流儀: viewBox 24・線 1.85・丸い端) */
function svg(inner: string): string {
  return (
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    inner +
    "</svg>"
  );
}

export const TOAST_ICON = {
  check: svg('<path d="M5 12.5l4.2 4.2L19 7"/>'),
  xmark: svg('<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>'),
  warn: svg('<path d="M12 3.8L2.8 20h18.4L12 3.8z"/><path d="M12 10v4.4"/><path d="M12 17.3v.01"/>'),
  info: svg('<circle cx="12" cy="12" r="8.8"/><path d="M12 11v5.4"/><path d="M12 7.9v.01"/>'),
} as const;

function iconByType(type: ToastType): string {
  switch (type) {
    case "success": return TOAST_ICON.check;
    case "error": return TOAST_ICON.xmark;
    case "warn": return TOAST_ICON.warn;
    default: return TOAST_ICON.info;
  }
}

function normType(t: unknown): ToastType {
  return t === "success" || t === "warn" || t === "error" ? t : "info";
}

/** 表示時間。短い成功は 3 秒、長い文・注意・エラー・行動つきは長く。 */
export function toastDuration(message: string, type: ToastType, hasAction: boolean): number {
  const len = Array.from(message).length;
  const base = type === "error" ? 5000 : type === "warn" ? 4500 : 3000;
  let ms = base + Math.max(0, len - 20) * 60;
  if (hasAction) ms = Math.max(ms, 6000);
  return Math.min(ms, type === "error" || type === "warn" ? 10000 : 8000);
}

let hostEl: HTMLElement | null = null;

function ensureHost(): HTMLElement {
  if (hostEl && document.body.contains(hostEl)) return hostEl;
  let el = document.getElementById("mpToastStack") as HTMLElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "mpToastStack";
    el.className = "mp-toast-stack";
    document.body.appendChild(el);
  }
  hostEl = el;
  return el;
}

/** 出す位置: PC はメインの列の中央、スマホは入力欄や通話バーがあればその上。 */
function placeHost(host: HTMLElement): void {
  const col = document.querySelector<HTMLElement>(".col-main");
  if (col && window.innerWidth >= 768) {
    const r = col.getBoundingClientRect();
    host.style.setProperty("--toast-cx", Math.round(r.left + r.width / 2) + "px");
  } else {
    host.style.removeProperty("--toast-cx");
  }
  // 画面の下に常駐する入力欄 (DM) や通話中のバーがあれば、その上に出す
  let extra = 0;
  document.querySelectorAll<HTMLElement>(".dm-composer, #dmComposer, .dm-call-bar:not([hidden])").forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.top < window.innerHeight && r.bottom > window.innerHeight - 200) {
      extra = Math.max(extra, window.innerHeight - r.top);
    }
  });
  // キーボードが出ている間 (入力中のエラーなど) はキーボードの上に出す。
  // iOS はキーボードでレイアウトを縮めないので、下に固定した知らせはそのままだと裏に沈む。
  const vv = window.visualViewport;
  if (vv) {
    const covered = window.innerHeight - (vv.offsetTop + vv.height * (vv.scale || 1));
    if (covered > 100) extra = Math.max(extra, covered);
  }
  host.style.setProperty("--toast-extra", extra + "px");
}

/**
 * 出ている間、右下の＋ボタン (FAB) を「知らせの上端 + 12px」まで持ち上げる (X / Material の
 * snackbar と同じ)。対象はトーストと、通知 ON の案内 (.mp-push-nudge) — 案内の「今はしない」に
 * ＋ボタンが被っていた (2026-09-21)。push-nudge.js も出し入れのたびにこれを呼ぶ。
 * 位置は動きの途中の見た目ではなく「落ち着いたときの位置」で測る。
 */
export function syncFabLift(): void {
  const root = document.documentElement;
  const fab = document.querySelector<HTMLElement>(".mp-fab");
  if (!fab || fab.offsetParent === null && getComputedStyle(fab).position !== "fixed") {
    root.classList.remove("has-snack");
    return;
  }
  const lifted = root.classList.contains("has-snack") ? parseFloat(root.style.getPropertyValue("--snack-h")) || 0 : 0;
  const fr = fab.getBoundingClientRect();
  const fabBottom = fr.bottom + lifted; // 持ち上げる前の下端
  let need = 0;
  const host = hostEl;
  if (host) {
    const hb = host.getBoundingClientRect().bottom;
    host.querySelectorAll<HTMLElement>(".mp-toast:not(.mp-toast--out)").forEach((t) => {
      need = Math.max(need, fabBottom - (hb - t.offsetHeight) + 12);
    });
  }
  document.querySelectorAll<HTMLElement>(".mp-push-nudge.mp-push-nudge--in").forEach((n) => {
    const bottom = parseFloat(getComputedStyle(n).bottom) || 0;
    const top = window.innerHeight - bottom - n.offsetHeight;
    need = Math.max(need, fabBottom - top + 12);
  });
  if (need > 0) {
    root.style.setProperty("--snack-h", Math.round(need) + "px");
    root.classList.add("has-snack");
  } else {
    root.classList.remove("has-snack");
  }
}

interface Live {
  el: HTMLElement;
  key: string;
  timer: number | null;
  remain: number;
  started: number;
}
let current: Live | null = null;

function arm(live: Live): void {
  if (live.timer) clearTimeout(live.timer);
  live.timer = null;
  if (live.remain <= 0) return;
  live.started = Date.now();
  live.timer = window.setTimeout(() => dismiss(live.el), live.remain);
}
function pause(live: Live): void {
  if (!live.timer) return;
  clearTimeout(live.timer);
  live.timer = null;
  live.remain = Math.max(1200, live.remain - (Date.now() - live.started));
}

export function showToast(message: string, type: ToastType = "info", opts: ToastOpts = {}): HTMLElement | null {
  // 文末の「。」は付けない (X と同じ短い言い切り)。途中の「。」はそのまま
  message = String(message || "").trim().replace(/。$/, "");
  if (!message) return null;
  type = normType(opts.type || type);
  const host = ensureHost();
  placeHost(host);
  const key = type + "\u0000" + message;
  const duration = opts.duration ?? toastDuration(message, type, !!opts.action);

  // 同じ文がもう出ていれば出し直さない (連打で同じ知らせが何枚も揺れない)
  if (current && current.key === key && current.el.isConnected && !current.el.classList.contains("mp-toast--out")) {
    current.remain = duration;
    arm(current);
    return current.el;
  }
  // 前のものは引っ込める (同時に出すのは 1 つだけ)
  host.querySelectorAll<HTMLElement>(".mp-toast:not(.mp-toast--out)").forEach((t) => dismiss(t));

  const toast = document.createElement("div");
  toast.className = `mp-toast mp-toast--${type}`;
  toast.setAttribute("role", type === "error" ? "alert" : "status");
  toast.title = message;

  const inner = document.createElement("div");
  inner.className = "mp-toast-inner";
  const ico = document.createElement("span");
  ico.className = "mp-toast-icon";
  ico.innerHTML = opts.icon || iconByType(type);
  const text = document.createElement("span");
  text.className = "mp-toast-text";
  text.textContent = message;
  inner.appendChild(ico);
  inner.appendChild(text);
  if (opts.action) inner.appendChild(actionLink(opts.action, toast));
  toast.appendChild(inner);
  host.appendChild(toast);

  const live: Live = { el: toast, key, timer: null, remain: duration, started: Date.now() };
  current = live;
  bindHold(toast, live);

  // 入場は次のフレームで (保険の setTimeout: 見えないタブで rAF が止まる)
  const go = () => {
    if (!toast.classList.contains("mp-toast--out")) toast.classList.add("mp-toast--in");
    syncFabLift();
  };
  requestAnimationFrame(go);
  window.setTimeout(go, 50);
  arm(live);
  return toast;
}

function actionLink(a: ToastAction, toast: HTMLElement): HTMLAnchorElement {
  const el = document.createElement("a");
  el.className = "mp-toast-earn mp-toast-act";
  el.href = a.href || "#";
  el.textContent = a.label;
  if (a.attrs) for (const k of Object.keys(a.attrs)) el.setAttribute(k, a.attrs[k]);
  el.addEventListener("click", (ev) => {
    if (a.onClick) {
      ev.preventDefault();
      a.onClick(ev);
    }
    dismiss(toast);
  });
  return el;
}

/** 触っている間は消さない / 下へ払えば消える */
function bindHold(toast: HTMLElement, live: Live): void {
  const hold = () => pause(live);
  const release = () => { if (!toast.classList.contains("mp-toast--out")) arm(live); };
  toast.addEventListener("mouseenter", hold);
  toast.addEventListener("mouseleave", release);
  toast.addEventListener("focusin", hold);
  toast.addEventListener("focusout", release);
  let y0 = 0, dy = 0, tracking = false;
  toast.addEventListener("touchstart", (e) => {
    if (e.touches.length !== 1) return;
    tracking = true; y0 = e.touches[0].clientY; dy = 0;
    hold();
  }, { passive: true });
  toast.addEventListener("touchmove", (e) => {
    if (!tracking) return;
    dy = Math.max(0, e.touches[0].clientY - y0);
    toast.style.transition = "none";
    toast.style.transform = dy > 0 ? `translateY(${dy}px)` : "";
  }, { passive: true });
  const end = () => {
    if (!tracking) return;
    tracking = false;
    toast.style.transition = "";
    toast.style.transform = "";
    if (dy > 28) dismiss(toast);
    else release();
  };
  toast.addEventListener("touchend", end, { passive: true });
  toast.addEventListener("touchcancel", end, { passive: true });
}

export function dismiss(toast: HTMLElement): void {
  if (toast.classList.contains("mp-toast--out")) return;
  if (current && current.el === toast) {
    if (current.timer) clearTimeout(current.timer);
    current = null;
  }
  toast.classList.remove("mp-toast--in");
  toast.classList.add("mp-toast--out");
  syncFabLift();
  const rm = () => { toast.remove(); syncFabLift(); };
  toast.addEventListener("transitionend", rm, { once: true });
  window.setTimeout(rm, 420);
}

/** ページ自身がその知らせを本文の中に出しているか (出していればトーストは重ねない) */
function shownInPage(text: string): boolean {
  if (document.querySelector("[data-flash-inpage]")) return true;
  const cands = document.querySelectorAll<HTMLElement>(
    '[class*="flash"], .settings-success, .alert, [role="alert"], [role="status"]');
  for (let i = 0; i < cands.length; i++) {
    const el = cands[i];
    if (el.closest("#mpToastStack, #mpTopNotice")) continue;
    if ((el.textContent || "").indexOf(text) >= 0) return true;
  }
  return false;
}

/** 行動のリンク先はこのサイトの中だけ (「/」で始まり「//」ではない) */
function safeHref(h: string | null): string {
  return h && /^\/(?!\/)/.test(h) ? h : "";
}

/**
 * サーバからの ?flash=... を出してから URL を掃除する。
 *   ?flash=<文>&flash_type=success|info|warn|error[&flash_act=<行動の名前>&flash_href=</で始まる行き先>]
 * ⚠ 出さないもの:
 *   - 英字だけの「鍵」(例: updated / created) — そのページ自身が意味のある文に訳して出す約束。
 *     訳さずに出すと英語の「updated」がそのままトーストになる (2026-09-21 に実物で確認)。
 *   - ページが自分で同じ知らせを本文に出している場合 — 二重になる。
 * 読み直しを挟む操作 (ピン留めなど) は mpToastAfterReload で次のページへ持ち越す。
 */
export function processFlashFromURL(): void {
  showCarried();
  const u = new URL(window.location.href);
  adoptPageNotices(u);
  const msg = u.searchParams.get("flash");
  if (!msg) return;
  const type = normType(u.searchParams.get("flash_type") || "success");
  // 「日本語の前置き: 英語の詳細」は前置きだけ (サーバの flashText と同じ規則。古い経路の保険)
  // ⚠ 「:」の後に空白があるものだけ (「〜9/28 00:00」の時刻を削らない)、英字を含むものだけ
  const text = msg.trim().replace(/(?::\s+|：\s*)(?=[\x20-\x7E]*[A-Za-z])[\x20-\x7E]+$/, "").trim();
  const isKey = /^[A-Za-z0-9_.:-]+$/.test(text);
  const act = (u.searchParams.get("flash_act") || "").trim();
  const href = safeHref(u.searchParams.get("flash_href"));
  if (text && !isKey && !shownInPage(text)) {
    showToast(text, type, act && href ? { action: { label: act, href } } : {});
  }
  ["flash", "flash_type", "flash_act", "flash_href"].forEach((k) => u.searchParams.delete(k));
  const newUrl = u.pathname + (u.searchParams.toString() ? "?" + u.searchParams.toString() : "") + u.hash;
  // 刻印 (history.state.mp) を落とすと、戻ったときに softnav が深さ不明と判断して
  // スタックを全 pop する (= 1 回の戻るで 2 画面戻る)。必ず台帳経由で置換する。
  const H = (window as unknown as { MoriokaHistory?: { replaceState(p: unknown, u: string): void } }).MoriokaHistory;
  if (H && typeof H.replaceState === "function") H.replaceState(null, newUrl);
  else window.history.replaceState(window.history.state, "", newUrl);
}

/**
 * サーバが本文に描いた「成功の知らせ」を下のトーストへ引き取る (X と同じく、結果は下に出す)。
 *   <div class="settings-success" data-notice-toast="success">保存しました</div>
 *   data-notice-toast="auto" … 種類は URL の flash_type (無ければ成功)
 * ⚠ 失敗・注意はページの中に残す (消えては困るので引き取らない)。JS が動かなければ帯のまま読める。
 */
function adoptPageNotices(u: URL): void {
  const els = document.querySelectorAll<HTMLElement>("[data-notice-toast]");
  let shown = false;
  els.forEach((el) => {
    let t = el.getAttribute("data-notice-toast") || "success";
    if (t === "auto") t = u.searchParams.get("flash_type") || "success";
    const type = normType(t);
    if (type === "error" || type === "warn") return;
    const text = (el.textContent || "").replace(/\s+/g, " ").trim();
    el.remove();
    if (!shown && text) { showToast(text, type); shown = true; }
  });
}

/* 読み直しを挟む操作の知らせを、読み直した後のページで出す */
const CARRY_KEY = "mp_toast_next";
export function toastAfterReload(message: string, type: ToastType = "success"): void {
  try { sessionStorage.setItem(CARRY_KEY, JSON.stringify({ m: message, t: type, at: Date.now() })); } catch (_) { /* ignore */ }
}
function showCarried(): void {
  try {
    const raw = sessionStorage.getItem(CARRY_KEY);
    if (!raw) return;
    sessionStorage.removeItem(CARRY_KEY);
    const v = JSON.parse(raw) as { m?: string; t?: string; at?: number };
    if (v && v.m && typeof v.at === "number" && Date.now() - v.at < 8000) showToast(v.m, normType(v.t));
  } catch (_) { /* ignore */ }
}

// 手書きの JS からも呼べるように。第 3 引数で行動も渡せる。
declare global {
  interface Window {
    showToast?: (msg: string, type?: ToastType, opts?: ToastOpts) => HTMLElement | null;
  }
}
window.showToast = (msg, type, opts) => showToast(msg, normType(type), opts || {});
(window as unknown as { mpSyncFabLift?: () => void }).mpSyncFabLift = syncFabLift;
(window as unknown as { mpToastAfterReload?: typeof toastAfterReload }).mpToastAfterReload = toastAfterReload;
